export type MarketingNavItemId =
  | 'home'
  | 'product'
  | 'partners'
  | 'howWeWork'
  | 'security'
  | 'integrations'
  | 'contact';

type MarketingNavItem = {
  id: MarketingNavItemId;
  href: string;
  isAnchor: boolean;
};

/** Same nav items on landing and inner marketing pages. */
export const MARKETING_NAV_ITEMS: MarketingNavItem[] = [
  { id: 'home', href: '/', isAnchor: false },
  { id: 'product', href: '/product', isAnchor: false },
  { id: 'partners', href: '/partners', isAnchor: false },
  { id: 'howWeWork', href: '/how-we-work', isAnchor: false },
  { id: 'security', href: '/security', isAnchor: false },
  { id: 'integrations', href: '/integrations', isAnchor: false },
  { id: 'contact', href: '#contact', isAnchor: true },
];

export type MarketingNavLabels = Record<MarketingNavItemId, string>;

export function buildMarketingNavLabels(
  pagesNav: {
    home: string;
    product: string;
    partners: string;
    integrations: string;
    security: string;
    contact: string;
  },
  landingNav: { how: string; contact: string }
): MarketingNavLabels {
  return {
    home: pagesNav.home,
    product: pagesNav.product,
    partners: pagesNav.partners,
    howWeWork: landingNav.how,
    security: pagesNav.security,
    integrations: pagesNav.integrations,
    contact: pagesNav.contact,
  };
}

export type ResolvedMarketingNavLink = {
  id: MarketingNavItemId;
  href: string;
  label: string;
  isAnchor: boolean;
};

export function getMarketingNavLinks(labels: MarketingNavLabels): ResolvedMarketingNavLink[] {
  return MARKETING_NAV_ITEMS.map((item) => ({
    id: item.id,
    href: item.href,
    label: labels[item.id],
    isAnchor: item.isAnchor,
  }));
}

/** On homepage, same-page anchors use hash-only hrefs for smooth scroll. */
export function resolveMarketingNavHref(
  link: ResolvedMarketingNavLink,
  pathname: string
): string {
  if (link.id === 'contact') return '#contact';
  if (pathname !== '/') {
    return link.href;
  }
  return link.href;
}

export function isMarketingNavLinkActive(
  pathname: string,
  hash: string,
  link: ResolvedMarketingNavLink
): boolean {
  const normalizedHash = hash.startsWith('#') || hash === '' ? hash : `#${hash}`;

  switch (link.id) {
    case 'home':
      return pathname === '/' && normalizedHash !== '#contact';
    case 'howWeWork':
      return pathname === '/how-we-work' || pathname.startsWith('/how-we-work/');
    case 'contact':
      return normalizedHash === '#contact';
    case 'product':
      return pathname === '/product' || pathname.startsWith('/product/');
    case 'partners':
      return pathname === '/partners' || pathname.startsWith('/partners/');
    case 'security':
      return pathname === '/security' || pathname.startsWith('/security/');
    case 'integrations':
      return pathname === '/integrations' || pathname.startsWith('/integrations/');
    default:
      return false;
  }
}

export const marketingNavLinkClass = (active: boolean) =>
  active
    ? 'text-sm font-semibold text-slate-900 underline decoration-cadrino-primary decoration-2 underline-offset-[6px]'
    : 'text-sm text-cadrino-muted transition-colors duration-200 hover:text-slate-900';

export const marketingNavLinkClassMobile = (active: boolean) =>
  active
    ? 'text-sm font-semibold text-slate-900'
    : 'text-sm text-cadrino-muted';
